<template>
  <v-container fluid>
    <v-form ref="form" @submit.prevent="save(false)">
      <v-row>
        <form-header :title="isEditing ?'Modifica esercizio' : 'Nuovo esercizio'"
                     @back="$router.replace('/exercises')" @saveback="save(true)" :loading="loading"></form-header>
        <v-col cols="12" >
          <h3>Info generali</h3>
        </v-col>
        <v-col cols="12" lg="6">
          <v-text-field
              label="Titolo esercizio*"
              v-model="editingItem.title.it"
              :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" class="py-0"></v-col>
        <v-col cols="12" lg="6">
          <v-textarea
              label="Descrizione*"
              v-model="editingItem.description.it"
              counter
              :rules="[rules.required]"
          ></v-textarea>
        </v-col>
        <v-col cols="12" >
          <h3>Media dell'esercizio</h3>
        </v-col>
        <v-col cols="12" md="6" lg="4">
          <media-field label="Video" media-type="video" v-model="editingItem.video_id" required :aspect-ratio="16/9"></media-field>
        </v-col>
        <v-col cols="12" md="6" lg="4">
          <media-field label="Audio" media-type="audio" v-model="editingItem.audio_id" required ></media-field>
        </v-col>

        <v-col cols="12" >
          <h3>Parte del del video in loop</h3>
        </v-col>
        <v-col cols="12" md="6" lg="4" xl="3">
          <v-text-field label="Inizio loop (secondi)" v-model="start" min="0" :max="videoLoopMax-1" step="1"
                        :rules="[rules.required, rules.minInt(0), rules.maxInt(videoLoopMax-1)]"></v-text-field>
        </v-col>
        <v-col cols="12" md="6" lg="4" xl="3">
          <v-text-field label="Fine loop (secondi)" v-model="end" :min="editingItem.video_loop_start+1" :max="videoLoopMax" step="1"
                        :rules="[rules.required, rules.minInt(editingItem.video_loop_start+1), rules.maxInt(videoLoopMax)]" ></v-text-field>
        </v-col>
        <v-col cols="12" class="py-0"></v-col>
        <v-col cols="12" md="6" lg="4">
          <video v-if="videoSrc" :src="videoSrc" ref="videoLoop" style="pointer-events: none; aspect-ratio: 16/9; width: 100%" autoplay loop muted
                 @durationchange="onVideoLoopMetadata" @timeupdate="onVideoLoopProgress"></video>
        </v-col>

        <form-footer @back="$router.replace('/exercises')" @saveback="save(true)" :loading="loading" ></form-footer>
      </v-row>
    </v-form>
  </v-container>
</template>

<script lang="ts">
import utils from "../utils";
import rules from "../utils/rules";
import FormHeader from "@/components/FormHeader.vue";
import FormFooter from "@/components/FormFooter.vue";
import {computed, inject, nextTick, onMounted, ref, useTemplateRef} from "vue";
import type {Auth} from "@/plugins/auth.ts";
import {type Messages, Msg} from "@/plugins/messages.ts";
import type {Globals} from "@/plugins/globals.ts";
import {useRoute, useRouter} from "vue-router";
import api from "@/api";
import {VForm} from "vuetify/components";
import type { Exercise } from "@/models.ts";
import MediaField from "@/components/MediaField.vue";
export default {
  components: {MediaField, FormFooter, FormHeader},
  setup() {
    const auth = inject('auth') as Auth
    const messages = inject('messages') as Messages
    const globals = inject('globals') as Globals
    const route = useRoute()
    const router = useRouter()
    const form = useTemplateRef<typeof VForm>('form')
    const videoLoop = useTemplateRef<HTMLVideoElement>('videoLoop')
    const loading = ref(false)

    const videoLoopMax = ref(1)
    const editingItem = ref<Exercise>({
      audio_id: "",
      description: {it:''},
      title: {it:''},
      video_id: "",
      video_loop_end: 0,
      video_loop_start: 0

    })

    const loadData = async () => {
      loading.value = true;
      let id = route.params.id as string;
      const res = await api.get<Exercise>('exercises', id)
      if(!res.success) {
        loading.value = false;
        messages.error(res.message ?? Msg.serverError)
        await router.replace('/exercises')
        return
      }
      editingItem.value = res.data!
      videoLoopMax.value = editingItem.value.video_loop_end;
      loading.value = false;
    }

    onMounted(async ()=>{
      await globals.waitForInit
      await router.isReady()
      await Promise.all([
          globals.refreshEmpty('media')
      ])
      if (route.params.id !== "new") {
        await loadData();
      }
    })




    return {
      form,
      loading,
      isEditing : computed(()=> route.params.id !== "new"),
      editingItem,
      rules,
      formatDate : utils.formatDate,
      start : computed<string|undefined>({
        get():string|undefined {
          return editingItem.value.video_loop_start.toString()
        },
        set(val:string|undefined) {
          editingItem.value.video_loop_start = utils.toInt(val, 0)
        }
      }),
      end : computed<string|undefined>({
        get():string|undefined {
          return editingItem.value.video_loop_end.toString()
        },
        set(val:string|undefined) {
          editingItem.value.video_loop_end = utils.toInt(val, 0)
        }
      }),
      videoSrc : computed<string|undefined>(()=>{
        if(!editingItem.value.video_id) return undefined
        const m = globals.media.value.find(i => i._id === editingItem.value.video_id)
        return m?.url
      }),
      videoLoopMax,
      onVideoLoopMetadata(e:Event) {
        console.log("durationchange", videoLoop.value!.duration)
        videoLoopMax.value = videoLoop.value!.duration
        if(editingItem.value.video_loop_end === 0) editingItem.value.video_loop_end = videoLoop.value!.duration
      },
      onVideoLoopProgress(e:Event) {
        //console.log(e.target.duration)
        const progress = videoLoop.value!.currentTime;
        if(progress >= editingItem.value.video_loop_end) {
          videoLoop.value!.currentTime = editingItem.value.video_loop_start
        }
      },
      async save(goBackAfterSave:boolean) {
        const v = await form.value!.validate()
        if(!v.valid) return;
        loading.value = true;
        const res = await api.save<Exercise>('exercises', editingItem.value)
        loading.value = false;
        if(!res.success) {
          messages.error(res.message ?? Msg.serverError)
          if(res.status === 401) {
            auth.logout()
          }
          return
        }
        editingItem.value = res.data!
        messages.success("esercizio salvato")
        if(goBackAfterSave) {
          await router.replace('/exercises')
        } else if(route.params.id === 'new'){
          await router.replace(`/exercises/${editingItem.value._id}`)
        }
      }
    };
  }
};
</script>

<style scoped></style>
